import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type Language = 'ro' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  getLocalizedPath: (path: string) => string;
  getCurrentPath: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [language, setLanguageState] = useState<Language>(() => {
    const path = window.location.pathname;
    return path.startsWith('/en') ? 'en' : 'ro';
  });

  useEffect(() => {
    const path = location.pathname;
    const detectedLang = path.startsWith('/en') ? 'en' : 'ro';
    if (detectedLang !== language) {
      setLanguageState(detectedLang);
    }
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const getLocalizedPath = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return language === 'en' ? `/en${cleanPath}` : cleanPath;
  };

  const getCurrentPath = (): string => {
    const path = location.pathname;
    return path.startsWith('/en') ? path.slice(3) || '/' : path;
  };

  const setLanguage = (lang: Language) => {
    const currentPath = getCurrentPath();
    const newPath = lang === 'en' ? `/en${currentPath}` : currentPath;
    navigate(newPath, { replace: true });
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, getLocalizedPath, getCurrentPath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
